import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface CustomerTimelineActivity {
  id: string;
  /** ISO timestamp for the semantic time element. */
  dateTime: string;
  /** Date and time already formatted for display by the caller. */
  dateLabel: string;
  /** Decorative icon shown on the shared timeline rail. */
  icon: ReactNode;
  content: ReactNode;
}

export interface CustomerTimelineProps {
  activities: readonly CustomerTimelineActivity[];
  /** When present, dim activities outside this set while keeping their place on the rail. */
  highlightedActivityIds?: ReadonlySet<string>;
  /** Appends skeleton rows to the loaded timeline while fetching another page. */
  isLoadingMore?: boolean;
}

const fadeStyle =
  "transition-opacity duration-[350ms] motion-reduce:transition-none";
const railSegmentStyle = twMerge(
  "absolute left-1/2 w-px -translate-x-1/2 bg-border-strong",
  fadeStyle,
);

const columnsStyle =
  "grid grid-cols-[2.5rem_2rem_minmax(0,1fr)] sm:grid-cols-[7rem_3rem_minmax(0,1fr)]";

// The skeleton pulses as one block, rails included, so bars skip `skeletonStyle`'s own pulse.
const skeletonBarStyle = "rounded-full bg-gray-200";

export interface CustomerTimelineSkeletonProps {
  /** Draws a rail into the first placeholder node from the preceding activity. */
  isConnected?: boolean;
}

/** Loading placeholder with the same columns and spacing as the timeline. */
export const CustomerTimelineSkeleton: FC<CustomerTimelineSkeletonProps> = (
  props,
) => (
  <section
    role="status"
    aria-label={
      props.isConnected
        ? "Loading more customer activity"
        : "Loading customer timeline"
    }
    className="min-w-0"
    style={{ maskImage: "linear-gradient(to bottom, black 60%, transparent)" }}
  >
    <div
      aria-hidden="true"
      className="animate-pulse motion-reduce:animate-none"
    >
      {[0, 1, 2].map((index) => (
        <div key={index} className={columnsStyle}>
          <div className="col-start-3 row-start-1 mb-1 sm:col-start-1 sm:mb-0 sm:pt-1">
            <div className={twMerge(skeletonBarStyle, "h-3 w-20")} />
          </div>
          <div className="relative col-start-2 row-start-1 row-end-3 flex justify-center sm:row-end-2">
            {props.isConnected && index === 0 && (
              <div className="absolute top-0 left-1/2 h-3 w-px -translate-x-1/2 bg-gray-200" />
            )}
            {index < 2 && (
              <div className="absolute top-6 bottom-0 left-1/2 w-px -translate-x-1/2 bg-gray-200" />
            )}
            <div className={twMerge(skeletonBarStyle, "relative size-6")} />
          </div>
          <div
            className={twMerge(
              "col-start-3 row-start-2 min-w-0 sm:row-start-1",
              index < 2 && "pb-8",
            )}
          >
            <div className="max-w-2xl space-y-3 pt-1">
              <div
                className={twMerge(skeletonBarStyle, "h-4 w-3/5 max-w-80")}
              />
              <div className={twMerge(skeletonBarStyle, "h-3 w-28")} />
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

interface YearGroup {
  year: string;
  startIndex: number;
  rows: { activity: CustomerTimelineActivity; index: number }[];
}

/**
 * Owns the rail; activity contents remain composable. Year labels pin at
 * `--customer-timeline-sticky-top` (default 0), which a page with pinned
 * headers above the list sets to where they end.
 */
export const CustomerTimeline: FC<CustomerTimelineProps> = (props) => {
  const { activities, highlightedActivityIds, isLoadingMore = false } = props;
  const yearGroups: YearGroup[] = [];
  for (const [index, activity] of activities.entries()) {
    const year = DateTime.fromISO(activity.dateTime).toFormat("yyyy");
    const group = yearGroups.at(-1);
    if (group?.year === year) {
      group.rows.push({ activity, index });
    } else {
      yearGroups.push({ year, startIndex: index, rows: [{ activity, index }] });
    }
  }
  const activeRows = activities.map(
    (activity) =>
      highlightedActivityIds === undefined ||
      highlightedActivityIds.has(activity.id),
  );
  // Keep the rail between highlighted nodes, even across dimmed rows.
  const firstActiveIndex = activeRows.indexOf(true);
  const lastActiveIndex = activeRows.lastIndexOf(true);
  const hasRailAbove = (index: number) =>
    index > firstActiveIndex && index <= lastActiveIndex;
  const hasRailBelow = (index: number) =>
    index >= firstActiveIndex && index < lastActiveIndex;

  return (
    <section aria-label="Customer timeline" className="min-w-0">
      {activities.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ol className="m-0 list-none p-0">
          {yearGroups.map(({ year, startIndex, rows }) => (
            <li key={startIndex} className={columnsStyle}>
              {/* Spans the whole year, so it stays pinned until the next
                  year pushes it off. */}
              <div className="sticky top-[var(--customer-timeline-sticky-top,0px)] z-[2] col-start-1 row-start-1 row-end-3 h-8 self-start text-xs leading-5 font-semibold text-foreground before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-5 before:bg-card after:pointer-events-none after:absolute after:inset-x-0 after:top-5 after:h-3 after:bg-linear-to-b after:from-card after:to-transparent">
                <span className="block bg-card">{year}</span>
              </div>
              <div
                aria-hidden="true"
                className="relative col-start-2 row-start-1 h-8"
              >
                <span
                  className={twMerge(
                    railSegmentStyle,
                    "inset-y-0",
                    !hasRailAbove(startIndex) && "opacity-0",
                  )}
                />
              </div>
              <ol className="col-span-full row-start-2 m-0 list-none p-0">
                {rows.map(({ activity, index }) => {
                  const isDimmed = !activeRows[index];
                  const isLastActivity = index === activities.length - 1;
                  const isLoadingContinuation = isLastActivity && isLoadingMore;
                  const isLast = isLastActivity && !isLoadingMore;

                  return (
                    <li key={activity.id} className={columnsStyle}>
                      <time
                        dateTime={activity.dateTime}
                        className={twMerge(
                          "col-start-3 row-start-1 mb-1 text-xs whitespace-nowrap text-muted-foreground sm:col-start-1 sm:mb-0 sm:pt-1",
                          fadeStyle,
                          isDimmed && "opacity-20",
                        )}
                      >
                        {activity.dateLabel}
                      </time>
                      <div
                        aria-hidden="true"
                        className="relative col-start-2 row-start-1 row-end-3 flex justify-center sm:row-end-2"
                      >
                        {/* Each row draws its half of the rail on either side
                            of the node; the lower half runs through the
                            row's bottom padding. */}
                        <span
                          className={twMerge(
                            railSegmentStyle,
                            "top-0 h-3",
                            !hasRailAbove(index) && "opacity-0",
                          )}
                        />
                        <span
                          className={twMerge(
                            railSegmentStyle,
                            "top-3 bottom-0",
                            !hasRailBelow(index) &&
                              !isLoadingContinuation &&
                              "opacity-0",
                            isLoadingContinuation && "bg-gray-200",
                          )}
                        />
                        <span
                          className={twMerge(
                            "relative flex size-6 shrink-0 scale-100 items-center justify-center rounded-full bg-gray-50 text-muted-foreground transition-[opacity,scale] duration-[350ms] motion-reduce:transition-none",
                            isDimmed && "scale-95 opacity-0",
                          )}
                        >
                          {activity.icon}
                        </span>
                      </div>
                      <div
                        className={twMerge(
                          "col-start-3 row-start-2 min-w-0 sm:row-start-1",
                          !isLast && "pb-8",
                          fadeStyle,
                          isDimmed && "opacity-20",
                        )}
                      >
                        {activity.content}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </li>
          ))}
        </ol>
      )}
      {isLoadingMore && (
        <CustomerTimelineSkeleton isConnected={activities.length > 0} />
      )}
    </section>
  );
};
